import {useMutation, UseMutationOptions, useQuery, UseQueryOptions} from '@tanstack/react-query';
import {useAtomValue, useSetAtom} from 'jotai';
import {errorAtom, teamsUserCredentialAtom} from '../store';

// For GET requests
export function useApiQuery<TData = unknown>(
    path: string,
    options?: Omit<UseQueryOptions<TData, Error>, 'queryKey' | 'queryFn'>
) {
    const setError = useSetAtom(errorAtom);
    const teamsUserCredential = useAtomValue(teamsUserCredentialAtom);

    return useQuery<TData, Error>({
        queryKey: ['api', path],
        queryFn: async () => {
            if (!teamsUserCredential) {
                throw new Error('User credentials not available');
            }
            let accessToken;
            try {
                accessToken = await teamsUserCredential.getToken([]);
            } catch (tokenError: any) {
                throw new Error(`Authentication error: ${tokenError.message}`);
            }
            if (!accessToken) {
                throw new Error('Failed to acquire access token');
            }

            try {
                const response = await fetch(path, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${accessToken?.token}`,
                        'Content-Type': 'application/json'
                    }
                });

                if (!response.ok) {
                    throw new Error(await response.text());
                }

                const text = await response.text();
                if (text && text.trim().length > 0) {
                    try {
                        return JSON.parse(text) as TData;
                    } catch (e) {
                        console.error('Error parsing JSON:', e);
                        return null as TData;
                    }
                } else {
                    return null as TData;
                }
            } catch (error) {
                setError(error instanceof Error ? error.message : 'An unknown error occurred');
                throw error;
            }
        },
        ...options
    });
}

export function useApiMutation<TData = unknown, TVariables = unknown>(
    method: 'POST' | 'DELETE',
    options?: Omit<UseMutationOptions<TData, Error, { path: string; body?: TVariables }> & {
        skipErrorNotification?: boolean
    }, 'mutationFn'>
) {
    const setError = useSetAtom(errorAtom);
    const teamsUserCredential = useAtomValue(teamsUserCredentialAtom);

    return useMutation<TData, Error, { path: string; body?: TVariables }>({
        mutationFn: async ({path, body}) => {
            try {
                const accessToken = await teamsUserCredential?.getToken([]);

                const response = await fetch(path, {
                    method,
                    body: body ? JSON.stringify(body) : undefined,
                    headers: {
                        'Authorization': `Bearer ${accessToken?.token}`,
                        'Content-Type': 'application/json'
                    }
                });

                if (!response.ok) {
                    throw new Error(`${response.statusText ? response.statusText : 'Unknown error'}: ${response.status}`);
                }

                const text = await response.text();
                if (text && text.trim().length > 0) {
                    try {
                        return JSON.parse(text) as TData;
                    } catch (e) {
                        console.error('Error parsing JSON:', e);
                        return null as TData;
                    }
                } else {
                    return null as TData;
                }
            } catch (error) {
                if (!options?.skipErrorNotification) {
                    setError(error instanceof Error ? error.message : 'An unknown error occurred');
                }
                throw error;
            }
        },
        ...options
    });
}
